import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaTrash, FaPlus, FaFileExcel, FaSearch } from "react-icons/fa";
import Select from "react-select";
import AddEditRestaurantSubscriptionMaster from "./AddEditRestaurantSubscriptionMaster";
import { axiosMaster } from "../api/axiosInstance";

const RestaurantSubscriptionMaster = () => {

  /* ================= STATE ================= */
  const [groupList, setGroupList] = useState([]);
  const [restaurantList, setRestaurantList] = useState([]);
  const [data, setData] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ================= LOAD GROUPS ================= */
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await axiosMaster.get("/GroupMaster");

      const opts = (res.data || []).map((g) => ({
        value: g.groupId,
        label: g.groupName,
      }));

      setGroupList(opts);
    } catch (err) {
      console.error("Group fetch error", err);
    }
  };

  /* ================= FETCH DATA ================= */
  const fetchData = async (groupId, restId) => {
    if (!groupId || !restId) return;

    try {
      setLoading(true);

      const res = await axiosMaster.get(
        `/RestaurantSubscriptionMaster/GetByRestaurant?restId=${restId}`
      );

      setData(res.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= GROUP CHANGE ================= */
  const handleGroupChange = async (option) => {
    setSelectedGroup(option);
    setSelectedRestaurant(null);
    setRestaurantList([]);
    setData([]);

    if (!option) return;

    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${option.value}`
      );

      const restOpts = (res.data || []).map((r) => ({
        value: r.restId,
        label: r.restaurantName,
      }));

      setRestaurantList(restOpts);
    } catch (err) {
      console.error("Restaurant fetch error", err);
    }
  };

  /* ================= RESTAURANT CHANGE ================= */
  const handleRestaurantChange = (option) => {
    setSelectedRestaurant(option);
    setData([]);

    if (!option) return;

    fetchData(selectedGroup?.value, option?.value);
  };

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    return data.filter((item) =>
      String(item.subscriptionId).toLowerCase().includes(term)
    );
  }, [data, search]);

  /* ================= MAP ID → NAME ================= */
  const groupMap = useMemo(() => {
    const map = {};
    groupList.forEach((g) => {
      map[g.value] = g.label;
    });
    return map;
  }, [groupList]);

  const restaurantMap = useMemo(() => {
    const map = {};
    restaurantList.forEach((r) => {
      map[r.value] = r.label;
    });
    return map;
  }, [restaurantList]);

  /* ================= ACTIONS ================= */
  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this record?")) return;

    try {
      console.log("Delete id:", row.id);

      fetchData(selectedGroup?.value, selectedRestaurant?.value);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= EXCEL EXPORT ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      SubscriptionId: r.subscriptionId,
      Group: groupMap[r.groupId] || r.groupId,
      Restaurant: restaurantMap[r.restId] || r.restId,
      ValidFrom: new Date(r.validFrom).toLocaleDateString(),
      ValidTo: new Date(r.validTo).toLocaleDateString(),
      ActivePlan: r.isActivePlan ? "Yes" : "No",
      Status: r.status ? "Active" : "Inactive",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "RestaurantSubscriptionMaster.csv";
    link.click();
  };

  const isAddEnabled =
    selectedGroup?.value && selectedRestaurant?.value;

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 flex flex-col border border-slate-200 min-h-[calc(100vh-120px)]">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">

        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          Restaurant Subscription Master
        </h2>

        <div className="flex flex-wrap gap-2">

          {/* GROUP */}
          <div className="w-full sm:w-56">
            <Select
              options={groupList}
              value={selectedGroup}
              onChange={handleGroupChange}
              placeholder="Select group..."
              isClearable
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </div>

          {/* RESTAURANT */}
          <div className="w-full sm:w-56">
            <Select
              options={restaurantList}
              value={selectedRestaurant}
              onChange={handleRestaurantChange}
              placeholder="Select restaurant..."
              isClearable
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </div>

          {/* SEARCH */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56"
            />
          </div>

          {/* EXCEL */}
          <button
            onClick={exportExcel}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white bg-green-600 hover:bg-green-700"
          >
            <FaFileExcel />
            Excel
          </button>

          {/* ADD */}
          <button
            onClick={openAdd}
            disabled={!isAddEnabled}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white
            ${
              isAddEnabled
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            <FaPlus />
            Add
          </button>

        </div>
      </div>

      {/* TABLE */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse">

          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-2 border">Sub Id</th>
              <th className="p-2 border">Group</th>
              <th className="p-2 border">Restaurant</th>
              <th className="p-2 border">Valid From</th>
              <th className="p-2 border">Valid To</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (
              <tr>
                <td colSpan="7" className="text-center p-6">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-6">
                  No data found
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (

                <tr key={row.subscriptionId}>

                  <td className="p-2 border">{row.subscriptionId}</td>

                  <td className="p-2 border">
                    {groupMap[row.groupId] || row.groupId}
                  </td>

                  <td className="p-2 border">
                    {restaurantMap[row.restId] || row.restId}
                  </td>

                  <td className="p-2 border">
                    {new Date(row.validFrom).toLocaleDateString()}
                  </td>

                  <td className="p-2 border">
                    {new Date(row.validTo).toLocaleDateString()}
                  </td>

                  <td className="p-2 border">
                    {row.status ? "Active" : "Inactive"}
                  </td>

                  <td className="p-2 border">
                    <div className="flex gap-3">
                      <FaEdit
                        className="text-blue-600 cursor-pointer"
                        onClick={() => openEdit(row)}
                      />
                      <FaTrash
                        className="text-red-600 cursor-pointer"
                        onClick={() => handleDelete(row)}
                      />
                    </div>
                  </td>

                </tr>

              ))
            )}

          </tbody>

        </table>
      </div>

      {/* MODAL */}
      {formOpen && (
        <AddEditRestaurantSubscriptionMaster
          editingData={editing}
          groupId={selectedGroup?.value}
          restId={selectedRestaurant?.value}
          onClose={() => setFormOpen(false)}
          onSuccess={() =>
            fetchData(selectedGroup?.value, selectedRestaurant?.value)
          }
        />
      )}

    </div>
  );
};

export default RestaurantSubscriptionMaster;